import { useEffect, useState } from "react";
import { campaignsAPI, templatesAPI } from "../../lib/api";
import { Megaphone, Plus, Send, Trash2, X, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Header } from '../../components/layout/Header';

const SEGMENTOS = [
    { value: "mulheres_com_proposito", label: "Mulheres com Propósito" },
    { value: "mesa_de_ester",          label: "Mesa de Ester" },
    { value: "luto_ao_recomeco",       label: "Do Luto ao Recomeço" },
    { value: "devocional",             label: "Devocional" },
    { value: "musica",                 label: "Música" },
    { value: "livro",                  label: "Livro" },
    { value: "geral",                  label: "Geral" },
];

const STATUS_COLORS = {
    rascunho: "bg-stone-700 text-stone-300",
    agendada: "bg-blue-900/40 text-blue-300",
    enviada:  "bg-green-900/40 text-green-300",
    cancelada:"bg-red-900/40 text-red-300",
};

function NewCampaignModal({ open, onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", segmentos: ["geral"], canal: "email", subject: "", content: "", status: "rascunho" });
    const [saving, setSaving] = useState(false);

    const toggleSeg = (v) => setForm(f => ({
        ...f, segmentos: f.segmentos.includes(v) ? f.segmentos.filter(s => s !== v) : [...f.segmentos, v]
    }));

    const save = async () => {
        if (!form.name || !form.content) { toast.error("Preencha nome e conteúdo"); return; }
        setSaving(true);
        try {
            await campaignsAPI.create(form);
            toast.success("Campanha criada");
            onCreated();
            onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
        finally { setSaving(false); }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-stone-900 border border-stone-700 rounded-2xl w-full max-w-lg shadow-2xl">
                <div className="p-5 border-b border-stone-800 flex items-center justify-between">
                    <h2 className="font-bold text-stone-100">Nova Campanha</h2>
                    <button onClick={onClose} className="text-stone-500 hover:text-stone-300"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Nome</label>
                        <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                            value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="ex: Lançamento Mesa de Ester 2026" />
                    </div>
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Canal</label>
                        <div className="flex gap-2">
                            {[["email","Email","text-sky-400"],["whatsapp","WhatsApp","text-green-400"]].map(([v,l,c]) => (
                                <button key={v} onClick={() => setForm(f => ({...f, canal: v}))}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border text-sm transition-colors
                                        ${form.canal === v ? "border-amber-600 bg-amber-900/20 text-amber-300" : "border-stone-700 text-stone-400 hover:text-stone-200"}`}>
                                    {v === "email" ? <Mail className={`w-4 h-4 ${c}`} /> : <MessageCircle className={`w-4 h-4 ${c}`} />}
                                    {l}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-stone-400 block mb-2">Segmentos de destino</label>
                        <div className="flex flex-wrap gap-2">
                            {SEGMENTOS.map(s => (
                                <button key={s.value} onClick={() => toggleSeg(s.value)}
                                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                        form.segmentos.includes(s.value)
                                            ? "bg-amber-800/40 text-amber-300 border-amber-700"
                                            : "bg-stone-800 text-stone-400 border-stone-700 hover:border-stone-600"
                                    }`}>
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {form.canal === "email" && (
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Assunto</label>
                            <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.subject} onChange={e => setForm(f => ({...f, subject: e.target.value}))} placeholder="Assunto do email" />
                        </div>
                    )}
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Conteúdo</label>
                        <textarea rows={5} className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600 resize-none"
                            value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))}
                            placeholder={form.canal === "email" ? "HTML ou texto do email..." : "Texto da mensagem WhatsApp..."} />
                    </div>
                </div>
                <div className="p-5 border-t border-stone-800 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-stone-400 hover:text-stone-200">Cancelar</button>
                    <button onClick={save} disabled={saving}
                        className="px-4 py-2 bg-amber-700 hover:bg-amber-600 text-white text-sm rounded-lg disabled:opacity-50 transition-colors">
                        {saving ? "Salvando..." : "Criar Campanha"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function CampaignsAdmin() {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [sending, setSending] = useState(null);

    const load = async () => {
        setLoading(true);
        try { const r = await campaignsAPI.list(); setCampaigns(r.data || []); }
        catch { toast.error("Erro ao carregar campanhas"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const sendCampaign = async (id, name) => {
        if (!window.confirm(`Enviar campanha "${name}" agora?`)) return;
        setSending(id);
        try {
            const r = await campaignsAPI.send(id);
            toast.success(`Enviada para ${r.data.sent} contatos`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar"); }
        finally { setSending(null); }
    };

    const deleteCampaign = async (id) => {
        if (!window.confirm("Excluir esta campanha?")) return;
        try { await campaignsAPI.delete(id); toast.success("Excluída"); load(); }
        catch { toast.error("Erro ao excluir"); }
    };

    return (
        <>
        <Header title="Campanhas" subtitle="Disparos pontuais por canal e segmento" actions={<button onClick={() => setShowNew(true)} className="flex items-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm px-4 py-2 rounded-lg transition-colors"><Plus className="w-4 h-4" /> Nova Campanha</button>} />
        <div className="p-6 space-y-5">
            <NewCampaignModal open={showNew} onClose={() => setShowNew(false)} onCreated={load} />

            {loading ? (
                <div className="text-stone-500 text-sm text-center py-16">Carregando...</div>
            ) : campaigns.length === 0 ? (
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-12 text-center">
                    <Megaphone className="w-10 h-10 text-stone-700 mx-auto mb-3" />
                    <p className="text-stone-400 text-sm">Nenhuma campanha criada.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {campaigns.map(camp => (
                        <div key={camp.id} className="bg-stone-900 border border-stone-800 rounded-xl p-4 flex items-center gap-4 hover:border-stone-700 transition-all">
                            <div className={`p-2 rounded-lg ${camp.canal === "whatsapp" ? "bg-green-900/30" : "bg-sky-900/30"}`}>
                                {camp.canal === "whatsapp"
                                    ? <MessageCircle className="w-4 h-4 text-green-400" />
                                    : <Mail className="w-4 h-4 text-sky-400" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-stone-100">{camp.name}</p>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[camp.status] || STATUS_COLORS.rascunho}`}>
                                        {camp.status}
                                    </span>
                                </div>
                                <div className="flex items-center gap-3 mt-0.5 text-xs text-stone-500">
                                    <span>{(camp.segmentos || []).join(", ")}</span>
                                    {camp.sent_count > 0 && <span>· {camp.sent_count} enviados</span>}
                                    {camp.open_count > 0 && <span>· {camp.open_count} aberturas</span>}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {camp.status !== "enviada" && (
                                    <button onClick={() => sendCampaign(camp.id, camp.name)}
                                        disabled={sending === camp.id}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-700 hover:bg-amber-600 text-white text-xs rounded-lg disabled:opacity-50 transition-colors">
                                        <Send className="w-3 h-3" />
                                        {sending === camp.id ? "Enviando..." : "Enviar"}
                                    </button>
                                )}
                                <button onClick={() => deleteCampaign(camp.id)}
                                    className="p-1.5 text-stone-500 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
        </>
    );
}
